/**
 * ProductoSearchSelect
 * Combobox buscable para seleccionar productos del catálogo.
 * - Búsqueda por substring en nombre, SKU y descripción
 * - Muestra precio de venta, stock con semáforo y SKU
 * - Accesible: flechas, Enter, Escape, clic fuera
 *
 * PROP theme="dark" | "light"  (default: "dark")
 *   "dark"  → modal oscuro (PresupuestoFormModal, FinancieroPage, etc.)
 *   "light" → modal blanco (FacturaFormModal)
 *
 * COLORES: usar siempre clases Tailwind del proyecto.
 *   Tema oscuro:  bg-arandu-dark / border-white/10 / text-white / text-slate-400
 *   Tema claro:   bg-white / border-gray-200 / text-gray-800 / text-gray-400
 *   NUNCA colores hexadecimales hardcodeados.
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search, Package, X, ChevronDown, Factory } from "lucide-react";

const SIMBOLO_MONEDA = { PYG: "₲", USD: "US$", BRL: "R$", ARS: "AR$", EUR: "€" };
// El precio se muestra en la moneda propia del producto (antes siempre ₲)
const fmtPrecio = (n, moneda = "PYG") => {
  if (!n && n !== 0) return "";
  const m = (moneda || "PYG").toUpperCase();
  if (m === "PYG") return `₲ ${Math.round(Number(n)).toLocaleString("es-PY")}`;
  return `${SIMBOLO_MONEDA[m] || m} ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const variantSearchText = (v = {}) => {
  const detalle = Array.isArray(v.atributos_detalle)
    ? v.atributos_detalle.flatMap(d => [
        d.atributo_nombre,
        d.opcion_nombre,
        `${d.atributo_nombre || ""} ${d.opcion_nombre || ""}`,
        `${d.atributo_nombre || ""}: ${d.opcion_nombre || ""}`,
      ])
    : [];
  return [
    v.atributos_texto,
    ...detalle,
    String(v.stock_actual ?? ""),
    String(v.precio_venta ?? ""),
    String(v.precio_costo ?? ""),
  ].filter(Boolean).join(" ");
};

// Tokens de color por tema
const T = {
  dark: {
    trigger:     "bg-slate-950 border-slate-600 text-white hover:border-slate-400 focus:border-arandu-blue/50",
    placeholder: "text-slate-300",
    icon:        "text-slate-400",
    chevron:     "text-slate-300",
    clearBtn:    "text-slate-500 hover:text-white",
    dropdown:    "bg-slate-950 border-slate-600",
    searchBar:   "border-slate-700",
    searchIcon:  "text-slate-400",
    searchInput: "text-white placeholder-slate-400",
    empty:       "text-slate-400",
    itemHover:   "hover:bg-slate-800",
    itemActive:  "bg-arandu-blue/25",
    itemSelected:"bg-arandu-blue/15",
    itemName:    "text-white",
    itemSku:     "text-slate-300",
    itemDesc:    "text-slate-300",
    priceTxt:    "text-slate-100",
    footer:      "border-slate-700 bg-slate-900",
    footerTxt:   "text-slate-300",
    dot:         "bg-arandu-blue",
  },
  light: {
    trigger:     "bg-white border-gray-200 text-gray-800 hover:border-gray-300 focus:border-blue-400",
    placeholder: "text-gray-400",
    icon:        "text-gray-400",
    chevron:     "text-gray-400",
    clearBtn:    "text-gray-400 hover:text-gray-700",
    dropdown:    "bg-white border-gray-200",
    searchBar:   "border-gray-100",
    searchIcon:  "text-gray-400",
    searchInput: "text-gray-800 placeholder-gray-400",
    empty:       "text-gray-400",
    itemHover:   "hover:bg-blue-50",
    itemActive:  "bg-blue-50",
    itemSelected:"bg-blue-50",
    itemName:    "text-gray-800",
    itemSku:     "text-gray-400",
    itemDesc:    "text-gray-400",
    priceTxt:    "text-gray-600",
    footer:      "border-gray-100 bg-gray-50",
    footerTxt:   "text-gray-400",
    dot:         "bg-blue-500",
  },
};

export default function ProductoSearchSelect({
  productos = [],
  value,
  onChange,
  placeholder = "Buscar producto...",
  className = "",
  theme = "dark",   // "dark" | "light"
  includeVariants = false,
  variantValue = "",
}) {
  const c = T[theme] ?? T.dark;

  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const [cursor, setCursor] = useState(-1);
  const containerRef        = useRef(null);
  const inputRef            = useRef(null);
  const listRef             = useRef(null);

  const selectedProd = productos.find(p => p.id === value);
  const selectedVariant = selectedProd?.variantes?.find(v => v.id === variantValue);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setQuery(""); setCursor(-1);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => { setCursor(-1); }, [query]);

  const options = useMemo(() => {
    const rows = [];
    productos.forEach(p => {
      const variantes = Array.isArray(p.variantes) ? p.variantes.filter(v => v.activo !== false) : [];
      if (includeVariants && p.variantes_habilitadas && variantes.length > 0) {
        variantes.forEach(v => rows.push({
          key: `${p.id}::${v.id}`,
          product: p,
          variant: v,
          label: `${p.nombre} - ${v.atributos_texto || "Variante"}`,
          sku: p.sku,
          descripcion: p.descripcion,
          codigo_fabricante: p.codigo_fabricante,
          variante_texto: variantSearchText(v),
          stock_actual: v.stock_actual ?? 0,
          precio_venta: v.precio_venta ?? p.precio_venta,
          precio_costo: v.precio_costo ?? p.precio_costo,
        }));
      } else {
        rows.push({
          key: p.id,
          product: p,
          variant: null,
          label: p.nombre,
          sku: p.sku,
          descripcion: p.descripcion,
          codigo_fabricante: p.codigo_fabricante,
          variante_texto: "",
          stock_actual: p.stock_actual ?? p.stock ?? 0,
          precio_venta: p.precio_venta,
          precio_costo: p.precio_costo,
        });
      }
    });
    return rows;
  }, [productos, includeVariants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt =>
      (opt.label             || "").toLowerCase().includes(q) ||
      (opt.sku               || "").toLowerCase().includes(q) ||
      (opt.descripcion       || "").toLowerCase().includes(q) ||
      (opt.variante_texto    || "").toLowerCase().includes(q) ||
      (opt.codigo_fabricante || "").toLowerCase().includes(q)
    );
  }, [query, options]);

  function handleSelect(option) {
    const prod = option?.product || null;
    const variant = option?.variant || null;
    onChange(prod ? prod.id : "", prod, variant);
    setOpen(false); setQuery(""); setCursor(-1);
  }

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown") { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === "Escape")     { setOpen(false); setQuery(""); return; }
    if (e.key === "ArrowDown")  { e.preventDefault(); setCursor(x => Math.min(x + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")  { e.preventDefault(); setCursor(x => Math.max(x - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (cursor >= 0 && filtered[cursor]) handleSelect(filtered[cursor]); }
  }

  useEffect(() => {
    if (cursor >= 0 && listRef.current) {
      listRef.current.querySelector(`[data-idx="${cursor}"]`)?.scrollIntoView({ block: "nearest" });
    }
  }, [cursor]);

  return (
    <div
      ref={containerRef}
      className={`relative arandu-product-select arandu-product-select--${theme === "light" ? "light" : "dark"} ${className}`}
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50); }}
        onKeyDown={handleKeyDown}
        className={`arandu-product-select__trigger w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm text-left transition-colors focus:outline-none ${c.trigger}`}
      >
        <span className={`min-w-0 flex-1 ${selectedProd ? "" : c.placeholder}`}>
          {selectedProd ? (
            <span className="flex items-center gap-2 min-w-0">
              <Package className={`w-3.5 h-3.5 flex-shrink-0 ${c.icon}`} />
              <span className="truncate min-w-0" title={selectedVariant?.atributos_texto ? `${selectedProd.nombre} - ${selectedVariant.atributos_texto}` : selectedProd.nombre}>
                {selectedProd.nombre}
              </span>
              {selectedVariant && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${
                  theme === "light"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
                }`}>
                  Variante
                </span>
              )}
              {selectedProd.sku && <span className={`text-[10px] font-mono flex-shrink-0 ${c.itemSku}`}>({selectedProd.sku})</span>}
            </span>
          ) : placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <span
              onClick={e => { e.stopPropagation(); handleSelect(null); }}
              className={`p-0.5 rounded cursor-pointer ${c.clearBtn}`}
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${c.chevron} ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`arandu-product-select__dropdown absolute z-[200] top-full left-0 right-0 mt-1 border rounded-xl shadow-2xl overflow-hidden ${c.dropdown}`}>
          {/* Buscador */}
          <div className={`arandu-product-select__search flex items-center gap-2 px-3 py-2 border-b ${c.searchBar}`}>
            <Search className={`w-3.5 h-3.5 flex-shrink-0 ${c.searchIcon}`} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nombre, SKU, cód. fabricante..."
              className={`arandu-product-select__input flex-1 bg-transparent text-xs outline-none ${c.searchInput}`}
              autoComplete="off"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className={c.clearBtn}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Lista */}
          <div ref={listRef} className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className={`px-3 py-4 text-center text-xs ${c.empty}`}>
                Sin resultados para "{query}"
              </div>
            ) : filtered.map((opt, i) => {
              const p = opt.product;
              const stock     = opt.stock_actual ?? 0;
              const stockBajo = stock <= 0;
              const isActive  = i === cursor;
              const isSelected = p.id === value && (!includeVariants || (opt.variant?.id || "") === (variantValue || ""));
              return (
                <div
                  key={opt.key}
                  data-idx={i}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setCursor(i)}
                  className={`arandu-product-select__item flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    isActive ? c.itemActive : isSelected ? c.itemSelected : c.itemHover
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {isSelected && <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />}
                      <span className={`text-xs font-medium truncate ${c.itemName}`}>{opt.label}</span>
                      {p.sku && <span className={`text-[10px] font-mono flex-shrink-0 ${c.itemSku}`}>({p.sku})</span>}
                    </div>
                    {opt.variant && (
                      <p className={`text-[10px] truncate mt-0.5 text-emerald-500`}>Variante de stock</p>
                    )}
                    {p.codigo_fabricante && (
                      <p className={`text-[10px] truncate mt-0.5 flex items-center gap-1 ${c.itemDesc}`}>
                        <Factory className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />
                        {p.codigo_fabricante}
                      </p>
                    )}
                    {!p.codigo_fabricante && p.descripcion && (
                      <p className={`text-[10px] truncate mt-0.5 ${c.itemDesc}`}>{p.descripcion}</p>
                    )}
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                    <span className={`text-[10px] font-medium ${
                      stockBajo ? "text-red-500" : stock <= 5 ? "text-amber-500" : "text-emerald-500"
                    }`}>
                      stock: {stock}
                    </span>
                    {opt.precio_venta > 0 && (
                      <span className={`text-[10px] font-mono ${c.priceTxt}`}>
                        {fmtPrecio(opt.precio_venta, p.moneda)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {filtered.length > 0 && (
            <div className={`px-3 py-1.5 border-t ${c.footer}`}>
              <p className={`text-[10px] ${c.footerTxt}`}>
                {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
                {query ? ` para "${query}"` : ""}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
